import React, { useState } from 'react';
import { List, useTable, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Select } from 'antd';

interface SubscriptionDto {
  id: string;
  userId: string;
  planId: string;
  // NOTE: this local shape shadows @wellverse/shared-ts's SubscriptionDto.
  billingCycle?: string;
  plan?: { name: string; planType: string } | null;
  status: string;
  provider: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd?: string | null;
  canceledAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const statusColors: Record<string, string> = {
  ACTIVE: '#3ecf8e',
  TRIALING: '#3b82f6',
  PAST_DUE: '#f59e0b',
  CANCELED: '#898989',
  EXPIRED: '#ef4444',
  PAUSED: '#8b5cf6',
};

const providerColors: Record<string, string> = {
  STRIPE: '#635bff',
  REVENUECAT: '#e04f7a',
  APPLE: '#000000',
  GOOGLE: '#34a853',
  MANUAL: '#898989',
};

export const SubscriptionList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [providerFilter, setProviderFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<SubscriptionDto>({
    resource: 'subscriptions',
    filters: {
      permanent: [
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
        ...(providerFilter ? [{ field: 'provider', operator: 'eq' as const, value: providerFilter }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="subscriptions">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 160 }}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Trialing', value: 'TRIALING' },
            { label: 'Past Due', value: 'PAST_DUE' },
            { label: 'Canceled', value: 'CANCELED' },
            { label: 'Expired', value: 'EXPIRED' },
            { label: 'Paused', value: 'PAUSED' },
          ]}
        />
        <Select
          placeholder="Provider"
          allowClear
          style={{ width: 160 }}
          onChange={(value) => setProviderFilter(value)}
          options={[
            { label: 'Stripe', value: 'STRIPE' },
            { label: 'RevenueCat', value: 'REVENUECAT' },
            { label: 'Apple', value: 'APPLE' },
            { label: 'Google', value: 'GOOGLE' },
            { label: 'Manual', value: 'MANUAL' },
          ]}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column dataIndex="userId" title="User ID" render={(v: string) => v.substring(0, 8) + '...'} />
        <Table.Column
          title="Plan"
          render={(_: any, record: SubscriptionDto) => record.plan?.name || record.planId.substring(0, 8) + '...'}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: string) => (
            <Tag style={tagStyle(statusColors[status] || '#898989')}>{status}</Tag>
          )}
        />
        <Table.Column
          dataIndex="billingCycle"
          title="Billing"
          render={(cycle?: string) => cycle ?? '—'}
        />
        <Table.Column
          dataIndex="provider"
          title="Provider"
          render={(provider: string) => (
            <Tag style={tagStyle(providerColors[provider] || '#898989')}>{provider}</Tag>
          )}
        />
        <Table.Column
          dataIndex="currentPeriodStart"
          title="Period Start"
          render={(v: string) => new Date(v).toLocaleDateString()}
        />
        <Table.Column
          dataIndex="currentPeriodEnd"
          title="Period End"
          render={(v: string) => new Date(v).toLocaleDateString()}
        />
        <Table.Column
          dataIndex="canceledAt"
          title="Canceled"
          render={(v: string | null) => v ? new Date(v).toLocaleDateString() : '-'}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: SubscriptionDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="subscriptions" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="subscriptions" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
